import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Inbox, AlertTriangle, Sparkles, Users, Wrench, CreditCard, UserCheck, Gift,
  TrendingUp, Calendar, Activity, Trophy, ChevronRight, Clock, RefreshCw,
} from "lucide-react";
import { api } from "../../lib/api";
import PendingApprovalsBanner from "../../components/PendingApprovalsBanner";
import LowStockBanner from "../../components/LowStockBanner";

/**
 * Comprehensive admin homepage. One-shot fetch of /admin/dashboard/v2,
 * auto-refreshes every 30s. Layout is a 12-col grid that collapses to 1
 * col on mobile.
 */
export default function AdminDashboardV2() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const go = (path) => nav(path);

  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const { data } = await api.get("/admin/dashboard/v2");
        if (alive) { setD(data); setLoading(false); }
      } catch {
        if (alive) setLoading(false);
      }
    }
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (loading) return <div className="text-sm text-[#a0b2af]">A carregar…</div>;
  if (!d) return <div className="text-sm text-[#fca5a5]">Não foi possível carregar o dashboard.</div>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard-v2">
      <header>
        <div className="eyebrow">Visão geral</div>
        <h1 className="font-display text-5xl mt-2">Dashboard.</h1>
        <p className="text-sm text-[#a0b2af] mt-2">
          Atualizado às {new Date(d.generated_at).toLocaleTimeString("pt-PT")}.
          Auto-refresh a cada 30s.
        </p>
      </header>

      {/* SECTION 1 — Alerts requiring action */}
      <PendingApprovalsBanner role="admin" />
      <LowStockBanner />
      <Alerts alerts={d.alerts} go={go} />

      {/* SECTION 2 — KPIs */}
      <section data-testid="dash-kpis">
        <div className="eyebrow text-[#a0b2af] mb-3">Mês atual</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Receita" value={`${d.kpis_month.revenue.toFixed(2)}€`} highlight
               to="/admin/graficos-financeiros" testId="kpi-revenue" />
          <Kpi label="Faturas pagas" value={d.kpis_month.invoices_paid}
               to="/admin/faturas" testId="kpi-invoices-paid" />
          <Kpi label="Faturas pendentes" value={d.kpis_month.invoices_pending}
               amber={d.kpis_month.invoices_pending > 0}
               to="/admin/faturas" testId="kpi-invoices-pending" />
          <Kpi label="Limpezas" value={d.kpis_month.bookings}
               to="/admin/reservas" testId="kpi-bookings" />
          <Kpi label="Ticket médio" value={`${d.kpis_month.avg_ticket.toFixed(2)}€`}
               to="/admin/graficos-financeiros" testId="kpi-avg-ticket" />
          <Kpi label="Despesas" value={`${d.kpis_month.expenses.toFixed(2)}€`} red
               to="/admin/produtos-gastos" testId="kpi-expenses" />
          <Kpi label="Lucro líquido" value={`${d.kpis_month.profit.toFixed(2)}€`}
               highlight={d.kpis_month.profit > 0} red={d.kpis_month.profit < 0}
               to="/admin/graficos-financeiros" testId="kpi-profit" />
          <Kpi label="Por aprovar" value={d.alerts.referrals_pending + d.alerts.extras_pending + (d.alerts.reschedule_pending || 0)}
               to="/admin/alteracoes" testId="kpi-pending-approvals" />
        </div>
      </section>

      {/* SECTION 2b — Margem real por limpeza (custo produtos + mão-de-obra) */}
      <MarginPerCleaningCard k={d.kpis_month} go={go} />

      {/* SECTION 2c — Limpezas via OTA esta semana (Booking/Airbnb/Vrbo) */}
      <OtaWeekCard data={d.ota_week} go={go} />

      {/* SECTION 3 — Revenue chart */}
      <RevenueChart data={d.revenue_chart} />

      {/* CTA — Gráficos financeiros interativos */}
      <Link
        to="/admin/graficos-financeiros"
        className="lux-card p-6 flex items-center gap-5 group border-[#d4af37]/30 hover:border-[#d4af37] transition-colors"
        data-testid="dash-finance-charts-cta"
      >
        <div className="w-12 h-12 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37]">
          <TrendingUp size={22} />
        </div>
        <div className="flex-1">
          <div className="font-display text-xl text-[#fdfbf7] group-hover:text-[#d4af37] transition-colors">
            Gráficos financeiros interativos
          </div>
          <div className="text-xs text-[#a0b2af] mt-1">
            Receitas, despesas, lucro líquido e taxas Stripe com filtros 7d / 30d / 12m.
          </div>
        </div>
        <ChevronRight size={20} className="text-[#d4af37]" />
      </Link>

      {/* Two-column band */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* SECTION 4 — Today */}
        <Today data={d.today} go={go} />
        {/* SECTION 5 — Next 7 days */}
        <Upcoming data={d.upcoming} go={go} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* SECTION 6 — Top clients */}
        <TopList title="Top 5 clientes do mês" rows={d.top_clients}
                 emptyMsg="Sem faturação este mês" icon={<Trophy size={14} />}
                 metric={(r) => `${r.revenue.toFixed(0)}€`}
                 sub={(r) => `${r.count} reservas`}
                 onClick={() => go("/admin/utilizadores")} testId="dash-top-clients" />
        {/* SECTION 7 — Top employees */}
        <TopList title="Top 5 funcionários do mês" rows={d.top_employees}
                 emptyMsg="Sem limpezas concluídas este mês" icon={<Trophy size={14} />}
                 metric={(r) => r.count}
                 sub={() => "limpezas"}
                 onClick={() => go("/admin/ranking")} testId="dash-top-employees" />
      </div>

      {/* SECTION 8 — Recent activity */}
      <RecentActivity rows={d.recent} go={go} />
    </div>
  );
}

/* -------------------------------- */
/* SUB-COMPONENTS                    */
/* -------------------------------- */

function Alerts({ alerts, go }) {
  const items = useMemo(() => [
    { key: "contacts_unread", label: "Pedidos de contacto", count: alerts.contacts_unread,
      icon: <Inbox size={20} />, link: "/admin/contactos", red: true },
    { key: "issues_open", label: "Problemas abertos", count: alerts.issues_open,
      icon: <AlertTriangle size={20} />, link: "/admin/problemas" },
    { key: "extras_pending", label: "Extras a aprovar", count: alerts.extras_pending,
      icon: <Sparkles size={20} />, link: "/admin/extras-sugeridos" },
    { key: "referrals_pending", label: "Indicações a aprovar", count: alerts.referrals_pending,
      icon: <Gift size={20} />, link: "/admin/promocao?tab=pendentes" },
    { key: "reschedule_pending", label: "Alterações a aprovar", count: alerts.reschedule_pending,
      icon: <Calendar size={20} />, link: "/admin/alteracoes", red: true },
    { key: "bookings_overdue", label: "Reservas atrasadas", count: alerts.bookings_overdue,
      icon: <Calendar size={20} />, link: "/admin/reservas?filter=overdue", red: true },
    { key: "maintenance_pending", label: "Manutenções pendentes", count: alerts.maintenance_pending,
      icon: <Wrench size={20} />, link: "/admin/manutencoes" },
    { key: "payments_overdue", label: "Pagamentos atrasados", count: alerts.payments_overdue,
      icon: <CreditCard size={20} />, link: "/admin/reservas?filter=pending_payment", red: true },
    { key: "employees_pending", label: "Funcionários por aprovar", count: alerts.employees_pending,
      icon: <UserCheck size={20} />, link: "/admin/utilizadores" },
  ].filter(x => x.count > 0), [alerts]);

  if (items.length === 0) {
    return (
      <div className="lux-card p-5 flex items-center gap-3 border-[#10b981]/30 bg-[#10b981]/5"
           data-testid="dash-alerts-clean">
        <span className="text-[#10b981] text-2xl">✓</span>
        <div>
          <div className="text-sm text-[#fdfbf7] font-semibold">Tudo em dia</div>
          <div className="text-xs text-[#a0b2af]">Nenhuma ação pendente neste momento.</div>
        </div>
      </div>
    );
  }
  return (
    <section data-testid="dash-alerts">
      <div className="eyebrow text-[#a0b2af] mb-3 flex items-center gap-2">
        <AlertTriangle size={12} /> Requer ação
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(it => (
          <button key={it.key} onClick={() => go(it.link)}
                  className={`lux-card p-4 text-left flex items-start gap-3 hover:scale-[1.02] transition-transform ${
                    it.red
                      ? "border-[#ef4444]/60 bg-[#ef4444]/5 animate-pulse-slow"
                      : "border-[#d4af37]/40 hover:border-[#d4af37]"
                  }`}
                  data-testid={`alert-${it.key}`}>
            <div className={`shrink-0 ${it.red ? "text-[#ef4444]" : "text-[#d4af37]"}`}>
              {it.icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-2xl text-[#fdfbf7]">{it.count}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] mt-0.5">{it.label}</div>
            </div>
            <ChevronRight size={14} className="text-[#a0b2af] shrink-0 mt-1" />
          </button>
        ))}
      </div>
    </section>
  );
}

function MarginPerCleaningCard({ k, go }) {
  if (!k || k.completed_count === 0) {
    return (
      <section className="lux-card p-5 border-[#1a4640]" data-testid="margin-per-cleaning">
        <div className="eyebrow text-[#a0b2af] mb-2">Margem real por limpeza</div>
        <div className="text-xs text-[#a0b2af]">
          Sem limpezas concluídas este mês. O KPI aparece assim que a primeira
          limpeza for marcada como «Concluída» e o consumo registado.
        </div>
      </section>
    );
  }
  const avgPct = Number(k.avg_margin_pct || 0);
  const threshold = Number(k.min_margin_pct_threshold || 40);
  const belowCount = Number(k.cleanings_below_margin || 0);
  const healthy = avgPct >= threshold;
  const tone = healthy ? "border-[#10b981]/50 bg-[#10b981]/5"
                       : avgPct > 0 ? "border-[#f59e0b]/50 bg-[#f59e0b]/5"
                       : "border-[#ef4444]/60 bg-[#ef4444]/10";
  const valueTone = healthy ? "text-[#10b981]"
                            : avgPct > 0 ? "text-[#f59e0b]"
                            : "text-[#fca5a5]";
  return (
    <section className={`lux-card p-5 ${tone} transition-colors`}
             data-testid="margin-per-cleaning">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow text-[#a0b2af] flex items-center gap-2">
            <TrendingUp size={12} /> Margem real por limpeza · {k.completed_count} concluídas
          </div>
          <div className={`font-display text-4xl mt-2 ${valueTone} tabular-nums`}
               data-testid="margin-avg-value">
            {Number(k.avg_margin_per_cleaning || 0).toFixed(2)}€
            <span className="text-base text-[#a0b2af] ml-2">/ limpeza</span>
          </div>
          <div className="text-xs text-[#a0b2af] mt-1">
            Margem média = receita − custo de produtos (consumo) − mão-de-obra
            ({k.hourly_rate_eur}€/h × duração real).
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Margem %</div>
          <div className={`font-display text-3xl ${valueTone} tabular-nums`}
               data-testid="margin-avg-pct">
            {avgPct.toFixed(1)}%
          </div>
          <div className="text-[10px] text-[#a0b2af] mt-0.5">
            alvo mínimo {threshold.toFixed(0)}%
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#1a4640]">
        <MiniStat label="Custo produtos" value={`${Number(k.product_cost_total || 0).toFixed(2)}€`}
                  testid="margin-products-cost" />
        <MiniStat label="Mão-de-obra" value={`${Number(k.labor_cost_total || 0).toFixed(2)}€`}
                  testid="margin-labor-cost" />
        <MiniStat label="Margem total" value={`${Number(k.margin_total || 0).toFixed(2)}€`}
                  testid="margin-total" tone={valueTone} />
      </div>

      {belowCount > 0 && (
        <button onClick={() => go("/admin/reservas?filter=completed")}
                className="mt-4 w-full text-left p-3 border border-[#ef4444]/50 bg-[#ef4444]/10 rounded-sm flex items-center justify-between hover:bg-[#ef4444]/15 transition-colors group"
                data-testid="margin-below-alert">
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-[#ef4444] shrink-0" />
            <div>
              <div className="text-sm text-[#fdfbf7]">
                <strong className="text-[#fca5a5]">{belowCount}</strong> limpeza
                {belowCount > 1 ? "s" : ""} com margem abaixo de {threshold.toFixed(0)}%
              </div>
              <div className="text-[10px] text-[#a0b2af]">
                Reveja preços, duração ou consumo — clique para ver as reservas concluídas.
              </div>
            </div>
          </div>
          <ChevronRight size={14} className="text-[#ef4444] group-hover:translate-x-0.5 transition-transform" />
        </button>
      )}
    </section>
  );
}

function MiniStat({ label, value, testid, tone }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className={`font-display text-lg mt-0.5 tabular-nums ${tone || "text-[#fdfbf7]"}`}>
        {value}
      </div>
    </div>
  );
}

const OTA_LABELS = {
  booking: "Booking.com",
  airbnb:  "Airbnb",
  vrbo:    "Vrbo / HomeAway",
  other:   "Outras (.ics)",
};
const OTA_TONES = {
  booking: "text-[#003580]",         // Booking blue
  airbnb:  "text-[#FF5A5F]",         // Airbnb red
  vrbo:    "text-[#3164FF]",         // Vrbo blue
  other:   "text-[#a0b2af]",
};

function OtaWeekCard({ data, go }) {
  const total = Number(data?.total || 0);
  const weekStart = data?.week_start;
  const weekEnd = data?.week_end;
  const bySource = data?.by_source || [];
  const empty = total === 0;
  return (
    <section className={`lux-card p-5 transition-colors ${empty ? "border-[#1a4640]" : "border-[#10b981]/40 bg-[#10b981]/5"}`}
             data-testid="ota-week-card">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow text-[#a0b2af] flex items-center gap-2">
            <RefreshCw size={12} /> Limpezas via OTA · semana atual
          </div>
          <div className={`font-display text-4xl mt-2 tabular-nums ${empty ? "text-[#fdfbf7]" : "text-[#10b981]"}`}
               data-testid="ota-week-total">
            {total} <span className="text-base text-[#a0b2af] ml-1">limpeza{total === 1 ? "" : "s"}</span>
          </div>
          <div className="text-xs text-[#a0b2af] mt-1">
            Importadas automaticamente dos calendários OTA entre {weekStart} e {weekEnd}.
          </div>
        </div>
        <button onClick={() => go("/admin/apartamentos")}
                className="btn-ghost-gold px-3 py-1.5 text-xs uppercase tracking-widest"
                data-testid="ota-week-manage">
          Gerir feeds OTA
        </button>
      </div>

      {empty ? (
        <div className="mt-4 pt-4 border-t border-[#1a4640] text-xs text-[#a0b2af]">
          Sem importações automáticas esta semana. Adicione um feed iCal num apartamento (botão «OTA») para começar a sincronizar Booking.com / Airbnb / Vrbo de 6 em 6 horas.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-[#1a4640]">
          {["booking", "airbnb", "vrbo", "other"].map(src => {
            const found = bySource.find(s => s.source === src);
            const count = found?.count || 0;
            return (
              <div key={src} data-testid={`ota-week-${src}`}>
                <div className={`text-[10px] uppercase tracking-widest ${OTA_TONES[src] || "text-[#a0b2af]"}`}>
                  {OTA_LABELS[src]}
                </div>
                <div className={`font-display text-2xl mt-0.5 tabular-nums ${count > 0 ? "text-[#fdfbf7]" : "text-[#a0b2af]/50"}`}>
                  {count}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}



function Kpi({ label, value, highlight, red, amber, to, testId }) {
  const c = red ? "text-[#fca5a5]" : amber ? "text-[#f59e0b]" : highlight ? "text-[#d4af37]" : "text-[#fdfbf7]";
  const cls = `lux-card p-4 block transition-all ${
    to ? "hover:border-[#d4af37] hover:shadow-[0_0_18px_rgba(212,175,55,0.18)] cursor-pointer focus:outline-none focus:border-[#d4af37]" : ""
  }`;
  const inner = (
    <>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${c}`}>{value}</div>
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId}>
        {inner}
      </Link>
    );
  }
  return <div className={cls} data-testid={testId}>{inner}</div>;
}

function RevenueChart({ data }) {
  const max = Math.max(1, ...data.map(d => d.revenue));
  return (
    <section data-testid="dash-revenue-chart">
      <div className="eyebrow text-[#a0b2af] mb-3 flex items-center gap-2">
        <TrendingUp size={12} /> Receita das últimas 6 semanas
      </div>
      <div className="lux-card p-5">
        <div className="flex items-end gap-3 h-40">
          {data.map((w) => {
            const h = (w.revenue / max) * 100;
            return (
              <div key={w.week} className="flex-1 flex flex-col items-center gap-2 min-w-0">
                <div className="text-[10px] text-[#d4af37] font-mono">
                  {w.revenue > 0 ? `${w.revenue.toFixed(0)}€` : ""}
                </div>
                <div className="w-full flex-1 flex items-end">
                  <div className="w-full bg-gradient-to-t from-[#d4af37] to-[#d4af37]/60 rounded-t-sm transition-all duration-700"
                       style={{ height: `${Math.max(2, h)}%` }} />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] truncate">
                  {w.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const STATUS_LABEL = {
  pending: "Pendente", confirmed: "Confirmada", in_progress: "Em curso",
  completed: "Concluída", cancelled: "Cancelada",
};

function Today({ data, go }) {
  const sections = [
    { title: "Confirmadas", items: data.confirmed, color: "#d4af37" },
    { title: "Em curso", items: data.in_progress, color: "#3b82f6" },
    { title: "Concluídas", items: data.completed, color: "#10b981" },
  ];
  return (
    <section className="lux-card p-5" data-testid="dash-today">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow text-[#a0b2af] flex items-center gap-2">
          <Calendar size={12} /> Hoje · {data.total} limpeza{data.total === 1 ? "" : "s"}
        </div>
        <button onClick={() => go("/admin/calendario")}
                className="text-[10px] uppercase tracking-widest text-[#d4af37] hover:underline">
          Calendário
        </button>
      </div>
      {data.total === 0 && (
        <div className="text-sm text-[#a0b2af] text-center py-6">Sem limpezas para hoje.</div>
      )}
      {sections.map(s => s.items.length > 0 && (
        <div key={s.title} className="mb-4 last:mb-0">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: s.color }}>
            {s.title} ({s.items.length})
          </div>
          <ul className="space-y-1.5">
            {s.items.slice(0, 6).map(b => (
              <li key={b.id} className="flex items-center gap-3 text-sm border-l-2 pl-3 py-1"
                  style={{ borderColor: s.color }}>
                <span className="font-mono text-[#d4af37] text-xs w-12">{b.scheduled_time}</span>
                <span className="text-[#fdfbf7] flex-1 truncate">
                  {b.apt_type} · {b.client_name || "—"}
                </span>
                {b.employee_name && (
                  <span className="text-[10px] text-[#a0b2af] truncate hidden sm:inline">
                    {b.employee_name}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function Upcoming({ data, go }) {
  const palette = { free: "#1a4640", low: "#3b82f6", med: "#d4af37", high: "#ef4444" };
  return (
    <section className="lux-card p-5" data-testid="dash-upcoming">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow text-[#a0b2af] flex items-center gap-2">
          <Calendar size={12} /> Próximos 7 dias
        </div>
        <button onClick={() => go("/admin/capacidade")}
                className="text-[10px] uppercase tracking-widest text-[#d4af37] hover:underline">
          Capacidade
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {data.map(d => {
          const dt = new Date(d.date + "T00:00:00");
          const dow = dt.toLocaleDateString("pt-PT", { weekday: "short" });
          return (
            <button key={d.date}
                    onClick={() => go(`/admin/calendario?date=${d.date}`)}
                    className="lux-card p-2 text-center hover:border-[#d4af37] transition-colors"
                    data-testid={`upcoming-${d.date}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{dow}</div>
              <div className="font-display text-xl text-[#fdfbf7] my-1">{dt.getDate()}</div>
              <div className="h-1.5 w-full rounded-full" style={{ background: palette[d.load] }} />
              <div className="text-[10px] text-[#a0b2af] mt-1">
                {d.count > 0 ? `${d.count} lim.` : "—"}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-3 text-[10px] text-[#a0b2af] mt-3 flex-wrap">
        <Legend color={palette.low} label="Leve" />
        <Legend color={palette.med} label="Moderado" />
        <Legend color={palette.high} label="Lotado" />
      </div>
    </section>
  );
}

function Legend({ color, label }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}

function TopList({ title, rows, emptyMsg, icon, metric, sub, onClick, testId }) {
  return (
    <section className="lux-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow text-[#a0b2af] flex items-center gap-2">{icon} {title}</div>
        <button onClick={onClick} className="text-[10px] uppercase tracking-widest text-[#d4af37] hover:underline">
          Ver tudo
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-[#a0b2af] text-center py-6">{emptyMsg}</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.id} className="flex items-center gap-3">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-display ${
                i === 0 ? "bg-[#d4af37] text-[#010c0a]" : "bg-[#062c27] text-[#a0b2af]"
              }`}>{i + 1}</span>
              <span className="flex-1 min-w-0 truncate text-sm text-[#fdfbf7]">{r.name}</span>
              <span className="text-right">
                <div className="text-sm text-[#d4af37] font-display">{metric(r)}</div>
                <div className="text-[10px] text-[#a0b2af]">{sub(r)}</div>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function RecentActivity({ rows, go }) {
  const ICONS = {
    booking: <Calendar size={12} className="text-[#d4af37]" />,
    contact: <Inbox size={12} className="text-[#3b82f6]" />,
    payment: <CreditCard size={12} className="text-[#10b981]" />,
    issue: <AlertTriangle size={12} className="text-[#ef4444]" />,
  };
  return (
    <section data-testid="dash-recent">
      <div className="eyebrow text-[#a0b2af] mb-3 flex items-center gap-2">
        <Activity size={12} /> Atividade recente
      </div>
      {rows.length === 0 ? (
        <div className="lux-card p-6 text-center text-sm text-[#a0b2af]">Sem eventos recentes.</div>
      ) : (
        <ol className="lux-card p-2">
          {rows.map((r, i) => (
            <li key={i}>
              <button onClick={() => r.link && go(r.link)}
                      className="w-full flex items-start gap-3 text-left p-3 hover:bg-[#062c27]/50 transition-colors"
                      data-testid={`recent-${i}`}>
                <span className="mt-1">{ICONS[r.type] || <Clock size={12} />}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-[#fdfbf7] truncate">{r.title}</span>
                  <span className="block text-[10px] uppercase tracking-widest text-[#a0b2af] mt-0.5">
                    {r.created_at ? new Date(r.created_at).toLocaleString("pt-PT") : ""}
                  </span>
                </span>
                <ChevronRight size={12} className="text-[#a0b2af] mt-1.5" />
              </button>
              {i < rows.length - 1 && <div className="h-px bg-[#1a4640]/40 mx-3" />}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
